import React, { FC, ReactNode, useMemo, useState, useCallback } from 'react';
import { isMobileQuery } from '@higo/ui';
import { FormattedMessage } from 'react-intl';
import { useEachMinute } from '@higo/common/src/hooks/useEachMinute';
import { calculateWaitingTime } from '@higo/common/src/utils/dateTimeUtils';
import { InfoBarListContainer } from './InfoBarListContainer';
import { InfoBarListItem } from './InfoBarListItem';
import { InfoBarLabeledValue } from './InfoBarLabeledValue';
import useMedia from 'react-use/lib/useMedia';
import styled from 'styled-components';
import { format, isValid } from 'date-fns';

interface ExaminationPageInfoBarForVisitsProps {
  examinationCreateDate?: string | number | Date;
  executedBy?: {
    firstName?: string;
    lastName?: string;
    email?: string | null;
    contactDetails?: any[] | null;
  } | undefined;
  shareModalSlot?: ReactNode;
  hasOperatorData?: boolean;
  partnerName?: string;
  isSharedPageForVisits?: boolean;
}

function parseISOStringNoTZ_AsUTC(s: string): Date | null {
  const m =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/.exec(
      s.trim(),
    );
  if (!m) return null;
  const [, y, mo, d, h, mi, se = '00', ms = '0'] = m;
  const ts = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(se),
    Number(ms.padEnd(3, '0')),
  );
  const date = new Date(ts);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toVisitDate(src?: string | number | Date | null): Date | null {
  if (src == null) return null;

  if (src instanceof Date) return isValid(src) ? src : null;

  if (typeof src === 'number') {
    const d = new Date(src);
    return isValid(d) ? d : null;
  }

  const s = String(src).trim();

  if (/[zZ]|[+\-]\d{2}:\d{2}$/.test(s)) {
    const d = new Date(s);
    return isValid(d) ? d : null;
  }

  return parseISOStringNoTZ_AsUTC(s);
}

const VISIT_DATE_FORMAT = 'dd/MM/yyyy HH:mm:ss';

function formatVisitDateLabel(src?: string | number | Date): string {
  const d = toVisitDate(src ?? null);
  return d ? format(d, VISIT_DATE_FORMAT) : '—';
}

const MobileInfoBarContainer = styled('div')`
  width: 95%;
  margin: 0.7rem 0.8rem 1.2rem 1.5rem;
  padding-top: 0.5rem;
`;

const OperatorDataMobile = styled('div')`
  display: block;
  &:after {
    content: '';
    display: block;
  }
`;

const ModalButtons = styled('div')`
  margin-left: auto;
  margin-right: 0;
  display: flex;
`;

export const ExaminationPageInfoBarForVisits: FC<ExaminationPageInfoBarForVisitsProps> =
  ({
    examinationCreateDate,
    executedBy,
    shareModalSlot,
    hasOperatorData,
    partnerName,
    isSharedPageForVisits,
  }) => {
    const isMobileView = useMedia(`${isMobileQuery}`);

    const formattedExamDate = useMemo(
      () => formatVisitDateLabel(examinationCreateDate),
      [examinationCreateDate],
    );

    const waitingBaseDate = useMemo(
      () => toVisitDate(examinationCreateDate),
      [examinationCreateDate],
    );

    const getWaitingTime = useCallback(
      () => (waitingBaseDate ? calculateWaitingTime(waitingBaseDate) : ''),
      [waitingBaseDate],
    );

    const [waiting, setWaiting] = useState<string>(getWaitingTime);
    const updateWaitingTime = useCallback(
      () => setWaiting(getWaitingTime),
      [getWaitingTime],
    );
    useEachMinute(updateWaitingTime);


    const cutMOName = (firstName?: string, lastName?: string) => {
      const full = `${lastName ?? ''} ${firstName ?? ''}`.trim();
      return full.length > 13 ? `${full.slice(0, 13)}...` : full || '—';
    };

    return !isMobileView ? (
      <div>
        <InfoBarListContainer>
          {!isSharedPageForVisits && (
            <InfoBarListItem>
              <InfoBarLabeledValue
                id="waiting-time"
                label={
                  <FormattedMessage id="examinationInfoBar.label.waitingTime" />
                }
              >
                {waiting || '—'}
              </InfoBarLabeledValue>
            </InfoBarListItem>
          )}

          <InfoBarListItem>
            <InfoBarLabeledValue
              id="exam-date"
              label={<FormattedMessage id="common.examData" />}
            >
              {formattedExamDate};{' '}
              {!isSharedPageForVisits &&
                (partnerName && partnerName.length > 13
                  ? `${partnerName.slice(0, 13)}...`
                  : partnerName ?? '')}
            </InfoBarLabeledValue>
            {shareModalSlot}
          </InfoBarListItem>

          {hasOperatorData && !isSharedPageForVisits && (
            <InfoBarListItem>
              <InfoBarLabeledValue
                id="medical-operator"
                label={
                  <FormattedMessage id="examinationInfoBar.label.executedBy" />
                }
              >
                {cutMOName(executedBy?.firstName, executedBy?.lastName)}
              </InfoBarLabeledValue>
            </InfoBarListItem>
          )}

        </InfoBarListContainer>
      </div>
    ) : (
      <MobileInfoBarContainer>
        <>
          {!isSharedPageForVisits && (
            <InfoBarListItem>
              <ModalButtons>{shareModalSlot}</ModalButtons>
            </InfoBarListItem>
          )}

          <InfoBarListItem>
            <InfoBarLabeledValue
              id="exam-date"
              label={<FormattedMessage id="common.examData" />}
            >
              {formattedExamDate};{' '}
              {!isSharedPageForVisits &&
                (partnerName && partnerName.length > 13
                  ? `${partnerName.slice(0, 13)}...`
                  : partnerName ?? '')}
            </InfoBarLabeledValue>
          </InfoBarListItem>

          <OperatorDataMobile>
            {!isSharedPageForVisits && (
              <InfoBarListItem>
                <InfoBarLabeledValue
                  id="waiting-time"
                  label={
                    <FormattedMessage id="examinationInfoBar.label.waitingTime" />
                  }
                >
                  {waiting || '—'}
                </InfoBarLabeledValue>
              </InfoBarListItem>
            )}

            {hasOperatorData && !isSharedPageForVisits && (
              <InfoBarListItem>
                <InfoBarLabeledValue
                  id="medical-operator"
                  label={
                    <FormattedMessage id="examinationInfoBar.label.executedBy" />
                  }
                >
                  {cutMOName(executedBy?.firstName, executedBy?.lastName)}
                </InfoBarLabeledValue>
              </InfoBarListItem>
            )}
          </OperatorDataMobile>
        </>
      </MobileInfoBarContainer>
    );
  };
